import React from 'react';
import { motion } from 'framer-motion';
import { Bell, Settings, Zap, BarChart2, Package, Users, DollarSign, ShoppingCart, FolderKanban, FileText, ArrowUpRight, ArrowDownRight } from 'lucide-react';
export function DashboardMockup() {
  return <motion.div className="relative w-full max-w-[800px] mx-auto animate-float" initial={{
    opacity: 0,
    y: 40
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    delay: 0.2
  }} data-id="element-32">
      {/* Browser Chrome */}
      <div className="bg-slate-900 rounded-2xl p-2 shadow-2xl border border-slate-800 overflow-hidden" data-id="element-33">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-3 py-2 mb-2" data-id="element-34">
          <div className="flex gap-1.5" data-id="element-35">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-36"></div>
            <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-37"></div>
            <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-38"></div>
          </div>

          <div className="flex-1 flex justify-center px-4" data-id="element-39">
            <div className="bg-slate-800 rounded-full px-4 py-1 flex items-center gap-2 text-xs text-slate-400 w-full max-w-sm" data-id="element-40">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-41"></div>
              app.akiosbos.com/dashboard
            </div>
          </div>

          <div className="flex gap-3 text-slate-400" data-id="element-42">
            <Bell className="w-4 h-4" data-id="element-43" />
            <Settings className="w-4 h-4" data-id="element-44" />
          </div>
        </div>

        {/* App Content */}
        <div className="bg-white rounded-xl overflow-hidden flex h-[500px]" data-id="element-45">
          {/* Sidebar */}
          <div className="w-56 border-r border-slate-100 flex flex-col bg-slate-50/50" data-id="element-46">
            <div className="p-4 border-b border-slate-100 flex items-center gap-3" data-id="element-47">
              <div className="bg-brand-blue text-white p-1.5 rounded-lg" data-id="element-48">
                <Zap className="w-5 h-5 fill-current" data-id="element-49" />
              </div>
              <div data-id="element-50">
                <div className="font-bold text-slate-900 text-sm leading-tight" data-id="element-51">
                  Akios BOS
                </div>
                <div className="text-[10px] text-slate-500" data-id="element-52">Business OS</div>
              </div>
            </div>

            <div className="p-3 flex-1 flex flex-col gap-1" data-id="element-53">
              <div className="flex items-center justify-between px-3 py-2 bg-blue-50 text-brand-blue rounded-lg text-sm font-medium" data-id="element-54">
                <div className="flex items-center gap-3" data-id="element-55">
                  <BarChart2 className="w-4 h-4" data-id="element-56" />
                  Overview
                </div>
                <div className="w-1.5 h-1.5 rounded-full bg-brand-blue" data-id="element-57"></div>
              </div>

              {[{
              icon: Package,
              label: 'Inventory'
            }, {
              icon: Users,
              label: 'CRM'
            }, {
              icon: DollarSign,
              label: 'Finance'
            }, {
              icon: ShoppingCart,
              label: 'Orders'
            }, {
              icon: FolderKanban,
              label: 'Projects'
            }, {
              icon: FileText,
              label: 'Reports'
            }].map(item => <div key={item.label} className="flex items-center gap-3 px-3 py-2 text-slate-500 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors cursor-pointer" data-id="element-58">
                  <item.icon className="w-4 h-4" data-id="element-59" />
                  {item.label}
                </div>)}
            </div>
          </div>

          {/* Main Area */}
          <div className="flex-1 p-6 overflow-y-auto bg-white" data-id="element-60">
            {/* Header */}
            <div className="flex justify-between items-start mb-6" data-id="element-61">
              <div data-id="element-62">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-63">
                  Good morning, Arjun <span className="text-xl" data-id="element-64">👋</span>
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-65">
                  April 14, 2026 — Here's your overview
                </p>
              </div>
              <button className="bg-brand-blue text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors" data-id="element-66">
                + New Order
              </button>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-4 gap-4 mb-6" data-id="element-67">
              {[{
              label: 'REVENUE',
              value: '₹12.4L',
              trend: '+18%',
              up: true
            }, {
              label: 'ORDERS',
              value: '2,841',
              trend: '+12%',
              up: true
            }, {
              label: 'CUSTOMERS',
              value: '1,208',
              trend: '+8%',
              up: true
            }, {
              label: 'TASKS DUE',
              value: '14',
              trend: '3 overdue',
              up: false,
              isAlert: true
            }].map(metric => <div key={metric.label} className="border border-slate-100 rounded-xl p-4 shadow-sm" data-id="element-68">
                  <div className="text-[10px] font-bold text-slate-400 tracking-wider mb-2" data-id="element-69">
                    {metric.label}
                  </div>
                  <div className="text-xl font-black text-slate-900 mb-2" data-id="element-70">
                    {metric.value}
                  </div>
                  <div className={`text-xs flex items-center gap-1 font-medium ${metric.isAlert ? 'text-red-500' : metric.up ? 'text-green-500' : 'text-slate-500'}`} data-id="element-71">
                    {metric.up ? <ArrowUpRight className="w-3 h-3" data-id="element-72" /> : <ArrowDownRight className="w-3 h-3" data-id="element-73" />}
                    {metric.trend}
                  </div>
                </div>)}
            </div>

            {/* Charts & Tables Area */}
            <div className="space-y-6" data-id="element-74">
              {/* Revenue Trend */}
              <div className="border border-slate-100 rounded-xl p-5 shadow-sm" data-id="element-75">
                <div className="flex justify-between items-center mb-6" data-id="element-76">
                  <h3 className="font-bold text-sm text-slate-900" data-id="element-77">
                    Revenue Trend
                  </h3>
                  <span className="text-xs text-brand-blue font-medium cursor-pointer" data-id="element-78">
                    Last 12 months
                  </span>
                </div>
                <div className="h-32 flex items-end justify-between gap-2" data-id="element-79">
                  {['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'].map((month, i) => {
                  // Generate a nice rising curve
                  const height = 30 + i * 5 + Math.random() * 15;
                  // Gradient intensity based on index
                  const opacity = 0.4 + i * 0.05;
                  return <div key={month} className="flex flex-col items-center gap-2 flex-1 group cursor-pointer" data-id="element-80">
                        <div className="w-full rounded-t-sm bg-brand-blue transition-all group-hover:bg-blue-700" style={{
                      height: `${height}%`,
                      opacity: opacity > 1 ? 1 : opacity
                    }} data-id="element-81"></div>
                        <span className="text-[9px] text-slate-400 font-medium" data-id="element-82">
                          {month}
                        </span>
                      </div>;
                })}
                </div>
              </div>

              {/* Recent Orders */}
              <div className="border border-slate-100 rounded-xl p-5 shadow-sm" data-id="element-83">
                <div className="flex justify-between items-center mb-4" data-id="element-84">
                  <h3 className="font-bold text-sm text-slate-900" data-id="element-85">
                    Recent Orders
                  </h3>
                  <span className="text-xs text-brand-blue font-medium cursor-pointer" data-id="element-86">
                    View all →
                  </span>
                </div>
                <div className="space-y-3" data-id="element-87">
                  {[{
                  id: '#ORD-8821',
                  client: 'Tech Solutions Ltd',
                  amount: '₹48,200',
                  status: 'Delivered',
                  statusColor: 'bg-green-50 text-green-700'
                }, {
                  id: '#ORD-8820',
                  client: 'Apex Retail',
                  amount: '₹12,850',
                  status: 'In Transit',
                  statusColor: 'bg-amber-50 text-amber-700'
                }, {
                  id: '#ORD-8819',
                  client: 'Modi Industries',
                  amount: '₹96,500',
                  status: 'Processing',
                  statusColor: 'bg-blue-50 text-blue-700'
                }].map(order => <div key={order.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-id="element-88">
                      <div data-id="element-89">
                        <div className="text-xs font-bold text-slate-900" data-id="element-90">
                          {order.id}
                        </div>
                        <div className="text-[10px] text-slate-500" data-id="element-91">
                          {order.client}
                        </div>
                      </div>
                      <div className="flex items-center gap-4" data-id="element-92">
                        <div className="text-sm font-bold text-slate-900" data-id="element-93">
                          {order.amount}
                        </div>
                        <div className={`text-[10px] font-bold px-2 py-1 rounded-md ${order.statusColor}`} data-id="element-94">
                          {order.status}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}